import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeClockIcon, AnankeLinkExternalIcon, AnankePersonIcon, AnankeTagIcon} from "~/domainModels/workspaceSpecific/icons";
import {
    AmaronEventTypes,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_COUNT__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

type AmaronCallEditRow =
    | {
          label: string;
          isEditable: boolean;
          columnType: "OttopilotEventContainer";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          columnType: "OttopilotEvent";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          columnType: "Auto";
          isAdditionalColumn: true;
          displayNames: string[];
          columnId?: Uuid;
      }
    | {
          label: string;
          isEditable: boolean;
          columnType: "OttopilotEvent";
          isAdditionalColumn: false;
          columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
      };

const AMARON_EVENT_ATTRIBUTION_EDIT_COLUMNS: Readonly<Record<string, ReadonlyArray<{columnId: Uuid; label: string}>>> = {
    [AmaronEventTypes.Calls]: [
        {columnId: AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID, label: "Google Ads Source"},
        {columnId: AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID, label: "Google Ads Campaign ID"},
        {columnId: AMARON__EVENT__CALLS__GBRAID__COLUMN_ID, label: "GBRAID"},
        {columnId: AMARON__EVENT__CALLS__GCLID__COLUMN_ID, label: "GCLID"},
        {columnId: AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID, label: "Facebook Ads Source"},
        {columnId: AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID, label: "Facebook Ads Campaign ID"},
        {columnId: AMARON__EVENT__CALLS__FBRAID__COLUMN_ID, label: "FBRAID"},
        {columnId: AMARON__EVENT__CALLS__FBCLID__COLUMN_ID, label: "FBCLID"},
    ],
    [AmaronEventTypes.Enquiry]: [
        {columnId: AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID, label: "Smartping Campaign"},
        {columnId: AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID, label: "Google Ads Source"},
        {columnId: AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID, label: "Google Ads Campaign ID"},
        {columnId: AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID, label: "GBRAID"},
        {columnId: AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID, label: "GCLID"},
        {columnId: AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID, label: "Facebook Ads Source"},
        {columnId: AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID, label: "Facebook Ads Campaign ID"},
        {columnId: AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID, label: "FBRAID"},
        {columnId: AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID, label: "FBCLID"},
    ],
    [AmaronEventTypes.Service]: [
        {columnId: AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID, label: "Google Ads Source"},
        {columnId: AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID, label: "Google Ads Campaign ID"},
        {columnId: AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID, label: "GBRAID"},
        {columnId: AMARON__EVENT__SERVICE__GCLID__COLUMN_ID, label: "GCLID"},
        {columnId: AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID, label: "Facebook Ads Source"},
        {columnId: AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID, label: "Facebook Ads Campaign ID"},
        {columnId: AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID, label: "FBRAID"},
        {columnId: AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID, label: "FBCLID"},
    ],
};

const AMARON_EVENT_ATTRIBUTION_EDIT_SECTION_IDS: Readonly<Record<string, Uuid>> = {
    [AmaronEventTypes.Calls]: uniqueUuid`amaron-call-edit-utm-attribution-fallback`,
    [AmaronEventTypes.Enquiry]: uniqueUuid`amaron-enquiry-edit-utm-attribution`,
    [AmaronEventTypes.Service]: uniqueUuid`amaron-service-edit-utm-attribution`,
};

/** Ensures every Amaron event edit panel exposes the eight attribution fields as read-only. */
export function ensureAmaronEventAttributionEditPanel(eventTemplate: OttopilotEventTemplate): OttopilotEventTemplate {
    const attributionColumns = AMARON_EVENT_ATTRIBUTION_EDIT_COLUMNS[eventTemplate.id];
    const editPanel = eventTemplate.configuration.eventPage.editPanel;
    if (attributionColumns === undefined || editPanel === undefined) {
        return eventTemplate;
    }

    const toPanelColumns = (columns: ReadonlyArray<{columnId: Uuid; label: string}>) =>
        columns.map((column) => ({
            id: `${String(column.columnId)}::${normalizeLabel(column.label)}` as Uuid,
            label: column.label,
            columnId: column.columnId,
            columnType: "OttopilotEvent" as const,
            eventTemplateId: eventTemplate.id,
            isAdditionalColumn: true as const,
            isEditable: false,
        }));
    const isUtmGroup = (displayName: string) =>
        ["utm parameter", "utm parameters"].includes(normalizeLabel(displayName));
    const hasUtmGroup = editPanel.columnGrouping.some((group) => isUtmGroup(group.displayName));
    const updatedColumnGrouping = editPanel.columnGrouping.map((group) => {
        if (!isUtmGroup(group.displayName)) {
            return group;
        }
        const existingColumnIds = new Set(group.columns.map((column) => String(column.columnId)));
        const missingColumns = attributionColumns.filter((column) => !existingColumnIds.has(String(column.columnId)));
        return missingColumns.length === 0
            ? group
            : {...group, columns: [...group.columns, ...toPanelColumns(missingColumns)]};
    });
    const columnGrouping = hasUtmGroup
        ? updatedColumnGrouping
        : [
              ...updatedColumnGrouping,
              {
                  id: AMARON_EVENT_ATTRIBUTION_EDIT_SECTION_IDS[eventTemplate.id]!,
                  displayName: "UTM Parameters",
                  iconId: AnankeLinkExternalIcon,
                  iconColor: "var(--ds-icon-accent-purple)",
                  iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                  initialIsOpen: true,
                  columns: toPanelColumns(attributionColumns),
              },
          ];

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    ...editPanel,
                    columnGrouping,
                },
            },
        },
    };
}

/**
 * Frontend-only: Enquiry Status stays visible on the edit panel but cannot be changed there.
 * Status changes continue via archive / disposition / automated call flows.
 */
export function buildAmaronEnquiryEditPanelTemplate(params: {
    eventTemplate: OttopilotEventTemplate;
}): OttopilotEventTemplate {
    const {eventTemplate} = params;
    if (eventTemplate.id !== (AmaronEventTypes.Enquiry as Uuid)) {
        return eventTemplate;
    }

    const editPanel = eventTemplate.configuration.eventPage.editPanel;
    if (editPanel === undefined) {
        return eventTemplate;
    }

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    ...editPanel,
                    columnGrouping: editPanel.columnGrouping.map((group) => ({
                        ...group,
                        columns: group.columns.map((column) =>
                            String(column.columnId) === String(AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID)
                                ? {...column, isEditable: false}
                                : column,
                        ),
                    })),
                },
            },
        },
    };
}

export function buildAmaronCallEditPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (AmaronEventTypes.Calls as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    const toSectionColumns = (rows: Array<AmaronCallEditRow>) => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `amaron-call-edit-${row.columnId}-${row.label}` as Uuid,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            isEditable: row.isEditable,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    isEditable: row.isEditable,
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`amaron-call-edit-customer-details`,
                            displayName: "Customer Details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    isEditable: true,
                                    columnType: "OttopilotEventContainer",
                                    columnId: AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name"],
                                },
                                {
                                    label: "Contact Number",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Customer State",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer State", "State"],
                                },
                                {
                                    label: "Customer City",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer City", "City"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`amaron-call-edit-call-details`,
                            displayName: "Call Details",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Call Status",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Status", "Status"],
                                },
                                {
                                    label: "L1 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L1 Disposition", "L1 disposition"],
                                },
                                {
                                    label: "L2 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L2 Disposition", "L2 disposition"],
                                },
                                {
                                    label: "L3 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L3 Disposition", "L3 disposition"],
                                },
                                {
                                    label: "L4 Disposition",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L4 Disposition", "L4 disposition"],
                                },
                                {
                                    label: "Remarks",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Remarks", "Disposition Remarks"],
                                },
                                {
                                    label: "Last Call Date",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Last Call Date", "Last call date"],
                                },
                                {
                                    label: "Call Count",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CALL_COUNT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Count", "call count"],
                                },
                                {
                                    label: "Assigned Agent",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Agent", "assigned agent"],
                                },
                                {
                                    label: "Action Platform",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "action platform", "Platform"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`amaron-call-edit-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Scheduled By",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {
                                    label: "Scheduled On",
                                    isEditable: true,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Logged By",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged By", "Logged by"],
                                },
                                {
                                    label: "Logged On",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged On", "Logged on"],
                                },
                                {
                                    label: "Cancelled By",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancelled On",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {
                                    label: "Cancellation Reason",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancellation Reason", "Cancellation Remarks", "Cancelation Reason"],
                                },
                                {label: "Modified By", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {label: "Modified On", isEditable: false, columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                            ]),
                        },
                        {
                            id: uniqueUuid`amaron-call-edit-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Utm Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source", "Utm Source"],
                                },
                                {
                                    label: "Utm Medium",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium", "Utm Medium"],
                                },
                                {
                                    label: "Utm Campaign",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign", "Utm Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term", "Utm Term"],
                                },
                                {
                                    label: "Utm Content",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content", "Utm Content"],
                                },
                                {label: "Google Ads Source", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gadSource", "gad_source", "Google Ads Source"]},
                                {label: "Google Ads Campaign ID", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gadCampaignId", "gad_campaignid", "Google Ads Campaign ID"]},
                                {label: "GBRAID", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GBRAID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gbraid", "GBRAID"]},
                                {label: "GCLID", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GCLID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gclid", "GCLID"]},
                                {label: "Facebook Ads Source", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fadSource", "fad_source", "Facebook Ads Source"]},
                                {label: "Facebook Ads Campaign ID", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fadCampaignId", "fad_campaignid", "Facebook Ads Campaign ID"]},
                                {label: "FBRAID", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FBRAID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fbraid", "FBRAID"]},
                                {label: "FBCLID", isEditable: false, columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FBCLID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fbclid", "FBCLID"]},
                            ]),
                        },
                    ],
                },
            },
        },
    };
}
